import { useQuery, useMutation } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../api/config';
import { shop } from '../../api/Endpoints';
import {
  AllPlans,
  FormulaBenefits,
  ProductFormula,
  ProductTest,
} from './Shop.model';
import { useGetShopPageTests } from '../../api/ContentFull/useContentful';
import {
  ProductFormulas,
  ProductTestKit,
  Promo,
} from '../../api/ContentFull/ContentfulTypes';
import { useGetShopPageFormulas } from '../../api/ContentFull/useContentful';

export type AllTestKitInfo = Partial<ProductTestKit> & ProductTest;
// Define a more specific type for Contentful product to avoid index signature issues

export type FormulaProduct = ProductFormulas & ProductFormula;
export type AllFormulaInfo = {
  tabName: string;
  title: Record<string, string>;
  subTitle: Record<string, string>;
  promo: Promo;
  couponCode: string;
  offerDuration: string;
  products: FormulaProduct[];
  discountPercentage: number;
};

class ShopDataError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShopDataError';
  }
}

// Base query for shop tests
export const useGetAllTestsQuery = () => {
  return useQuery<ProductTest[]>({
    queryKey: [QUERY_KEYS.SHOP_PAGE_TESTS],
    queryFn: async () => {
      const response = await shop.getShopProductTests();
      if (!response?.data) {
        throw new ShopDataError('Failed to fetch shop product tests');
      }
      return response.data;
    },
  });
};

type BaseQueryOptions = {
  enabled?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
};

export const useGetAllFormulasQuery = (options?: BaseQueryOptions) => {
  return useQuery<ProductFormula[]>({
    queryKey: [QUERY_KEYS.SHOP_PAGE_FORMULAS],
    queryFn: async () => {
      const response = await shop.getShopProductFormulas();
      if (!response?.data) {
        throw new ShopDataError('Failed to fetch shop product formulas');
      }
      return response.data;
    },
    ...options,
  });
};

// Combined query that merges shop tests with Contentful data
export const useMergedShopTests = () => {
  // Get shop tests
  const shopTests = useGetAllTestsQuery();

  // Get Contentful tests
  const contentfulTests = useGetShopPageTests();

  // Combine both results
  const mergedTestQuery = useQuery<AllTestKitInfo[]>({
    queryKey: [QUERY_KEYS.MERGED_SHOP_TESTS],
    queryFn: async () => {
      // Wait for both queries to complete
      const [shopData, contentfulData] = await Promise.all([
        shopTests.data,
        contentfulTests.data?.tests,
      ]);

      // Throw errors if data is missing
      if (!shopData) {
        throw new ShopDataError('Shop data is missing');
      }
      if (!contentfulData || !Array.isArray(contentfulData)) {
        throw new ShopDataError('Contentful test data is missing or invalid');
      }

      // Create a map of shop tests by test_id for faster lookup
      const shopTestMap = new Map<string, ProductTest>();
      shopData.forEach((shopTest) => {
        shopTestMap.set(shopTest.test_id.toString(), shopTest);
      });

      // Start with Contentful data and only include tests that have matching shop data
      const mergedTests = contentfulData
        .map((contentfulTest: ProductTestKit) => {
          const shopTest = shopTestMap.get(contentfulTest.testId);

          if (!shopTest) {
            console.warn(
              `Missing shop data for Contentful test ID: ${contentfulTest.testId} - skipping this test`,
            );
            return null;
          }

          // Merge data, keeping Contentful's structure but including shop's data
          const mergedTest: AllTestKitInfo = {
            ...contentfulTest,
            ...shopTest,
            test_id: shopTest.test_id, // Use shop's test_id as the canonical ID
          };

          return mergedTest;
        })
        .filter(Boolean) as AllTestKitInfo[]; // Filter out any nulls

      console.log(
        `✅ Successfully merged ${mergedTests.length} out of ${contentfulData.length} tests`,
      );

      return mergedTests;
    },
    enabled: shopTests.data !== undefined && contentfulTests.data !== undefined,
  });
  const isLoading =
    shopTests.isLoading ||
    contentfulTests.isLoading ||
    mergedTestQuery.isLoading;

  const isError =
    shopTests.isError || contentfulTests.isError || mergedTestQuery.isError;

  return {
    ...mergedTestQuery,
    isLoading,
    isError,
  };
};

export const useMergedShopFormulas = (options?: BaseQueryOptions) => {
  // Get shop formulas
  const shopFormulas = useGetAllFormulasQuery(options);

  // Get Contentful formulas
  const contentfulFormulas = useGetShopPageFormulas();

  // plans with combination of prices
  const allPlans = useGetAllPlansQuery();

  // Combine both results
  const mergedQuery = useQuery<AllFormulaInfo>({
    queryKey: [QUERY_KEYS.MERGED_SHOP_FORMULAS],
    queryFn: async () => {
      // Wait for both queries to complete
      const [shopData, contentfulData] = await Promise.all([
        shopFormulas.data,
        contentfulFormulas.data,
      ]);

      // Throw errors if data is missing
      if (!shopData) {
        throw new ShopDataError('Shop formula data is missing');
      }

      if (!contentfulData || !Array.isArray(contentfulData)) {
        throw new ShopDataError(
          'Contentful formula data is missing or invalid',
        );
      }

      // Create a map of shop formulas by product_id for faster lookup
      const shopFormulaMap = new Map<string, ProductFormula>();
      shopData.forEach((shopFormula: ProductFormula) => {
        shopFormulaMap.set(shopFormula.product_id.toString(), shopFormula);
      });

      // Return the first formula tab with merged data
      if (contentfulData.length === 0) {
        throw new ShopDataError('No shop formula tabs found');
      }

      // Extract tab information from first tab
      const contentfulShopFormula = contentfulData[0];
      const { tabName, title, subtitle, promo, products } =
        contentfulShopFormula;

      // Create merged formula tab
      const mergedFormulaTab: AllFormulaInfo = {
        tabName: tabName ?? '',
        title: title ?? {},
        subTitle: subtitle ?? {},
        promo: typeof promo === 'object' ? (promo as Promo) : ({} as Promo),
        couponCode: contentfulShopFormula.couponCode ?? '',
        offerDuration: contentfulShopFormula.offerDuration ?? '',
        products: [],
        discountPercentage: contentfulShopFormula.discountPercentage ?? 0,
      };

      // Process each product in the tab if there are any
      if (products && Array.isArray(products) && products.length > 0) {
        mergedFormulaTab.products = products
          .map((contentfulProduct) => {
            // Find matching shop formula by product_id
            const shopFormula = shopFormulaMap.get(contentfulProduct.productId);

            if (!shopFormula) {
              console.warn(
                `Missing shop data for Contentful product ID: ${contentfulProduct.productId}`,
              );
              return null;
            }

            // Merge data, keeping Contentful's structure but including shop's data
            return {
              ...contentfulProduct,
              ...shopFormula,
            } as FormulaProduct;
          })
          .filter(Boolean) as FormulaProduct[];
      }

      return mergedFormulaTab;
    },
    enabled:
      shopFormulas.data !== undefined && contentfulFormulas.data !== undefined,
  });

  const isLoading =
    shopFormulas.isLoading ||
    contentfulFormulas.isLoading ||
    mergedQuery.isLoading ||
    allPlans.isLoading;

  const isError =
    shopFormulas.isError ||
    contentfulFormulas.isError ||
    mergedQuery.isError ||
    allPlans.isError;

  return {
    ...mergedQuery,
    isLoading,
    isError,
    refetch: async () => {
      await Promise.all([
        shopFormulas.refetch(),
        contentfulFormulas.refetch(),
        allPlans.refetch(),
      ]);
      return await mergedQuery.refetch();
    },
  };
};

export const useGetAllPlansQuery = () => {
  return useQuery<AllPlans>({
    queryKey: [QUERY_KEYS.SHOP_PAGE_PLANS],
    queryFn: async () => {
      const response = await shop.getShopProductPlans();
      if (!response?.data) {
        throw new ShopDataError('Failed to fetch shop product plans');
      }
      return response.data;
    },
  });
};

export const useShopifyMultipass = () => {
  return useMutation({
    mutationFn: async (url: string) => {
      const response = await shop.getShopifyMultipass(url);
      if (!response?.data) {
        throw new ShopDataError('Failed to fetch shopify multipass');
      }
      return response.data;
    },
  });
};

export const useGetFormulaBenefits = (product_id: number) => {
  return useQuery<FormulaBenefits>({
    queryKey: [QUERY_KEYS.FORMULA_BENEFITS, product_id],
    queryFn: async () => {
      const response = await shop.getFormulaBenefits(product_id);
      if (!response?.data) {
        throw new ShopDataError('Failed to fetch formula benefits');
      }
      return response.data;
    },
  });
};
